import { getAgeInYears } from "./CprHelpers";

export type InputValidationType =
	| "name"
	| "cpr"
	| "cpr_age"
	| "text"
	| "none"
	| "phone"
	| "email"
	| "number";

// Backwards-compat alias so existing imports of TextInputValidationType keep working
export type TextInputValidationType = InputValidationType;

export interface ValidationResult {
	valid: boolean;
	error: string;
}

const nameRegex = /([a-zA-ZÆØÅæøå]|[à-úÀ-Ú]+\s?\b){2,}/;
const cprRegex = /^[0-9]{10}$|^[0-9]{6}[-][0-9]{4}$|^[0-9]{6}\s[0-9]{4}$/;
const phoneRegex = /^[0-9]{8}$/;
const numberRegex = /^\d+$/;
const emailRegex = /^[a-zA-ZÆØÅæøå0-9.!#%&'*+/=?^_`{|}~-]+@[a-zA-ZÆØÅæøå0-9-]+(?:\.[a-zA-ZÆØÅæøå0-9-]+)+/;

const DEFAULT_ERRORS: Record<string, string> = {
	name: "Indtast dit fulde navn",
	cpr: "Ugyldigt CPR-nummer indtastet",
	cpr_age_format: "Ugyldigt CPR-nummer indtastet",
	cpr_age_underage: "Du skal være over 18 år",
	text: "Feltet må ikke være tomt",
	phone: "Feltet er ikke et gyldigt telefonnummer",
	email: "Ugyldig e-mail indtastet",
	number: "Må kun indeholde tal",
};

export function stripCpr(value: string): string {
	return value.replace(/[-\s]/g, "");
}

export function validateTextInput(
	value: string,
	validationType: InputValidationType,
	customError?: string
): ValidationResult {
	if (!value) {
		if (validationType === "none") return { valid: true, error: "" };
		return { valid: false, error: "" };
	}

	switch (validationType) {
		case "name":
			if (!nameRegex.test(value)) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.name };
			}
			return { valid: true, error: "" };

		case "cpr": {
			if (!cprRegex.test(value)) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.cpr };
			}
			return { valid: true, error: "" };
		}

		case "cpr_age": {
			if (!cprRegex.test(value)) {
				return {
					valid: false,
					error: customError ?? DEFAULT_ERRORS.cpr_age_format,
				};
			}
			const stripped = stripCpr(value);
			if (getAgeInYears(stripped) < 18) {
				return {
					valid: false,
					error: customError ?? DEFAULT_ERRORS.cpr_age_underage,
				};
			}
			return { valid: true, error: "" };
		}

		case "text":
			if (value.trim().length === 0) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.text };
			}
			return { valid: true, error: "" };

		case "none":
			return { valid: true, error: "" };

		case "number":
			if (!numberRegex.test(value)) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.number };
			}
			return { valid: true, error: "" };

		case "phone":
			if (!phoneRegex.test(value)) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.phone };
			}
			return { valid: true, error: "" };

		case "email":
			if (!emailRegex.test(value)) {
				return { valid: false, error: customError ?? DEFAULT_ERRORS.email };
			}
			return { valid: true, error: "" };

		default:
			return { valid: true, error: "" };
	}
}

/**
 * Validates an email address pair (e.g. "enter email" + "repeat email").
 * Returns an array of Danish-language error messages; empty array = valid.
 */
export function validateEmailMatch(email: string, repeat: string): string[] {
	const errs: string[] = [];
	if (email || repeat) {
		if (!emailRegex.test(email) || !emailRegex.test(repeat)) {
			errs.push("Ugyldig e-mail(s) indtastet");
		}
		if (email !== repeat) {
			errs.push("Mails matcher ikke");
		}
	}
	return errs;
}
